import { formatCurrency } from 'src/helpers/formatCurrency';
import { Currency } from '@cryptoalgebra/kim-sdk';
import React, { useCallback, useMemo } from 'react';
import { useAddress, useBalance } from '@thirdweb-dev/react';
import { Address } from '@thirdweb-dev/sdk';
import { TokenIcon } from '@app/components/TokenIcon';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { getCurrencySymbol } from '@app/helpers/currency';
import { Token } from '@app/types/token';

interface EnterAmountsCardProps {
  currency: Currency | undefined;
  value: string;
  handleChange: (value: string) => void;
  isDisabled: boolean;
}

const EnterAmountCard = ({
  currency,
  value,
  handleChange,
  isDisabled
}: EnterAmountsCardProps) => {
  const account = useAddress();

  const { data: balance, isLoading } = useBalance(
    currency?.isNative ? undefined : (currency?.wrapped.address as Address)
  );

  const balanceString = useMemo(() => {
    if (isLoading || !balance) return 'Loading...';

    return formatCurrency.format(Number(balance.displayValue));
  }, [balance, isLoading]);

  const handleInput = useCallback(
    (value: string) => {
      if (value === '.') value = '0.';

      handleChange(value);
    },
    [handleChange]
  );

  function setMax() {
    handleChange(balance?.displayValue || '0');
  }

  return (
    <VStack
      w="100%"
      bg="neutral.1000"
      transition="background 0.2s ease"
      px="16px"
      py="12px"
      borderRadius="12px"
      overflow="hidden"
    >
      <Text
        textAlign="left"
        w="100%"
        color="neutral.300"
        fontSize="12px"
        lineHeight="18px"
        letterSpacing="-0.18px"
        mb="8px"
      ></Text>
      <HStack w="100%" mb="4px">
        <TokenIcon
          symbol={
            currency
              ? (getCurrencySymbol({
                  contractAddress: currency.wrapped.address,
                  symbol: currency.symbol
                } as Token) as string)
              : undefined
          }
          minW="32px"
          height="32px"
        />
        <Text
          textAlign="left"
          color="white"
          fontSize="16px"
          whiteSpace="nowrap"
        >
          {currency
            ? (getCurrencySymbol({
                contractAddress: currency.wrapped.address,
                symbol: currency.symbol
              } as Token) as string)
            : undefined}
        </Text>
        <DebouncedInput
          type="text"
          amount={value}
          onChange={val => {
            handleInput(val);
          }}
          placeholder={'0.0'}
          disabled={isDisabled}
        />
        <Button variant="row" onClick={setMax}>
          Max
        </Button>
      </HStack>
      {currency && account && (
        <HStack w="100%" justify="space-between">
          <HStack justify="flex-start">
            <Text
              color="neutral.500"
              fontSize="12px"
              fontWeight="400"
              lineHeight="18px"
              letterSpacing="-0.18px"
            >
              Balance:
            </Text>
            <Text
              color="neutral.100"
              fontSize="12px"
              fontWeight="400"
              lineHeight="18px"
              letterSpacing="-0.18px"
            >
              {balanceString}
            </Text>
          </HStack>
        </HStack>
      )}
    </VStack>
  );
};

export default EnterAmountCard;
